import { useCallback, useEffect } from 'react';
import { PaymentFormValues, TOnboardingPayment } from '@src/types';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useCountryOptions } from '@hooks/useCountryOptions.ts';
import { useStateOptions } from '@hooks/useStateOptions.ts';
import { ReviewCheckoutForm } from '@pages/user/checkout/ReviewCheckoutForm.tsx';
import { useCreatePaAccountCheckout } from '@stores/createPaAccountCheckoutStore.ts';
import {
  getCreatePaAccountAddon,
  submitCreatePaAccountCheckout,
} from '@src/services/paAccountProcess.ts';
import { CheckoutPayload, PaPaymentMode } from '@src/types/paAccountProcess.ts';

export const CreatePaAccountCheckoutPage = () => {
  const { platform, account, payment, setPayment, reset, setAccount } =
    useCreatePaAccountCheckout();
  const { countryOptions } = useCountryOptions();
  const { stateOptions } = useStateOptions();
  const navigate = useNavigate();
  const params = useParams();

  const fetchAddons = useCallback(async () => {
    if (
      !params.leeloo_user_id ||
      !params.leeloo_account_id ||
      !params.sub_type
    ) {
      return;
    }
    const res = await getCreatePaAccountAddon(
      params.sub_type,
      params.leeloo_user_id,
      params.leeloo_account_id
    );

    setAccount(res.data);
  }, [
    params.leeloo_account_id,
    params.leeloo_user_id,
    params.sub_type,
    setAccount,
  ]);

  useEffect(() => {
    fetchAddons();
  }, [fetchAddons]);

  const handleConfirm = useCallback(
    async (data: PaymentFormValues) => {
      try {
        const payload: TOnboardingPayment = {
          terms_accepted: payment?.terms_accepted || [],
          referral_source: data.referral_source,
          payment_type: data.payment_type,
          coupon_code: data.coupon_code,
          billing_info: {
            street: data.street,
            city: data.city,
            country: data.country,
            state: data.state,
            zip_code: data.zip_code,
            phone_number: data.phone_number,
            first_name: data.first_name,
            last_name: data.last_name,
          },
        };

        setPayment(payload);

        if (
          !params.leeloo_user_id ||
          !params.leeloo_account_id ||
          !params.sub_type
        ) {
          return;
        }

        const checkoutPayload: CheckoutPayload = {
          leeloo_user_uuid: params.leeloo_user_id,
          leeloo_account_uuid: params.leeloo_account_id,
          payment_mode: params.sub_type as PaPaymentMode,
          payment_gateway: data.payment_type,
          billing_info: {
            street: data.street,
            city: data.city,
            country: data.country,
            state: data.state,
            zip_code: data.zip_code,
            phone_number: data.phone_number,
            first_name: data.first_name,
            last_name: data.last_name,
          },
        };

        const res = await submitCreatePaAccountCheckout(checkoutPayload);

        reset();
        if (!res.data?.url) {
          navigate('/', { replace: true });

          return;
        }
        window.location.href = res.data.url;
      } catch {
        toast.error('Something went wrong. Please try again later.');
      }
    },
    [
      navigate,
      params.leeloo_account_id,
      params.leeloo_user_id,
      params.sub_type,
      reset,
      setPayment,
    ]
  );

  return (
    <ReviewCheckoutForm
      isCreatePaAccount
      account={account}
      countries={countryOptions}
      payment={payment}
      platform={platform}
      states={stateOptions}
      onBack={() => navigate('/renew/select-account')}
      onSubmit={handleConfirm}
    />
  );
};
